import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler } from 'react-hook-form';

import { IDemoSignUpParams } from '@aqx-core/common/api/user-account';
import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { createSignUpDemoSchema } from '@aqx-core/common/schemas/sign-up-demo-schema';
import { useSignUpDemoDefaultDeposit } from '@aqx-core/common/services/sign-up-demo';
import { signUpDemoViewController } from '@aqx-core/common/services/sign-up-demo/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';

export interface ISignUpDemoValues extends Omit<IDemoSignUpParams, 'phoneNumber'> {
  dialCode: string;
  localPhoneNumber: string;
  agree: boolean;
  country: string;
}

export const useSignUpDemoForm = ({
  defaultValues,
  withAuth
}: { defaultValues?: Partial<ISignUpDemoValues>; withAuth?: boolean } = {}) => {
  const schema = useMemo(() => createSignUpDemoSchema(), []);

  const resolver = useMemo(() => yupResolver(schema), [schema]);
  const useFormMethods = useBaseForm<ISignUpDemoValues>({
    resolver,
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false
  });
  const defaultDeposit = useSignUpDemoDefaultDeposit();
  const { handleSubmit, trigger, watch, formState, setValue } = useFormMethods;

  const [firstName, lastName, email, dialCode, localPhoneNumber, depositAmount, agree, country] = watch([
    'firstName',
    'lastName',
    'email',
    'dialCode',
    'localPhoneNumber',
    'depositAmount',
    'agree',
    'country'
  ]);

  const {
    errors: {
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
      dialCode: dialCodeError,
      localPhoneNumber: localPhoneNumberError,
      depositAmount: depositAmountError,
      country: countryError
    }
  } = formState;

  const isDisabled = useMemo(() => {
    const nameValidation = !firstName || !lastName || firstNameError || lastNameError;

    return !!(
      nameValidation ||
      !email ||
      !dialCode ||
      !localPhoneNumber ||
      !depositAmount ||
      !country ||
      emailError ||
      dialCodeError ||
      localPhoneNumberError ||
      depositAmountError ||
      countryError
    );
  }, [
    firstName,
    firstNameError,
    lastName,
    lastNameError,
    email,
    emailError,
    dialCode,
    dialCodeError,
    localPhoneNumber,
    localPhoneNumberError,
    depositAmount,
    depositAmountError,
    country,
    countryError
  ]);

  const onError = useCallback((message: string) => {
    snackbarController.showAlert({
      message,
      messageType: EUISnackBarMessageType.ERROR
    });
  }, []);

  const handleFormSubmit: SubmitHandler<ISignUpDemoValues> = useCallback(
    (values) => {
      const { dialCode, localPhoneNumber, ...rest } = values;
      const phoneNumber = `${dialCode} ${localPhoneNumber}`;

      signUpDemoViewController.openDemoAccount({ params: { phoneNumber, ...rest }, onError, withAuth });
    },
    [onError, withAuth]
  );

  const submit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      handleSubmit(handleFormSubmit)(e);
    },
    [handleSubmit, handleFormSubmit]
  );

  useEffect(() => {
    if (defaultDeposit) {
      setValue('depositAmount', defaultDeposit);
    }
  }, [defaultDeposit, setValue]);

  useEffect(() => {
    if (dialCode && localPhoneNumber) {
      trigger('localPhoneNumber');
    }
  }, [dialCode, localPhoneNumber, trigger]);

  return {
    depositAmount,
    dialCode,
    email,
    firstName,
    isDisabled,
    lastName,
    localPhoneNumber,
    agree,
    country,
    useFormMethods,
    submit,
    trigger
  };
};
